/**
 * Meetings API Functions
 */

import { api } from '@/lib/api';
import type {
  Agenda,
  AgendaSection,
  AgendaWithSections,
  AgendasResponse,
  AgendasQueryParams,
  Meeting,
  MeetingSection,
  MeetingAttendee,
  MeetingTicket,
  MeetingDetail,
  MeetingsResponse,
  MeetingsQueryParams,
  MeetingTranscript,
  CreateAgendaInput,
  CreateMeetingInput,
  UpdateMeetingInput,
  CreateSectionInput,
  UpdateAttendeeInput,
  AddTicketInput,
  MeetingType,
  MeetingStatus,
  MatchStatus,
  ToolType,
  TicketAction,
  V4Agenda,
  V4AgendaSection,
  V4AgendaWithSections,
  V4AgendasResponse,
  V4Meeting,
  V4MeetingSection,
  V4MeetingAttendee,
  V4MeetingTicket,
  V4MeetingDetail,
  V4MeetingsResponse,
  StaffUser,
  StaffUsersResponse,
  CalendarEventsResponse,
} from './types';

// =============================================================================
// Transform Functions
// =============================================================================

function transformV4Agenda(v4: V4Agenda): Agenda {
  return {
    id: v4.id,
    title: v4.title,
    team_id: v4.teamId,
    created_by: v4.createdBy,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
  };
}

function transformV4AgendaSection(v4: V4AgendaSection): AgendaSection {
  return {
    id: v4.id,
    agenda_id: v4.agendaId,
    name: v4.name,
    tool_type: v4.toolType as ToolType | null,
    duration_seconds: v4.durationSeconds,
    is_visible: v4.isVisible,
    is_locked: v4.isLocked,
    sort_order: v4.sortOrder,
  };
}

function transformV4AgendaWithSections(v4: V4AgendaWithSections): AgendaWithSections {
  return {
    ...transformV4Agenda(v4),
    sections: (v4.sections || []).map(transformV4AgendaSection),
  };
}

function transformV4Meeting(v4: V4Meeting): Meeting {
  return {
    id: v4.id,
    title: v4.title,
    agenda_id: v4.agendaId,
    team_id: v4.teamId,
    type: v4.type as MeetingType,
    status: v4.status as MeetingStatus,
    scheduled_at: v4.scheduledAt,
    started_at: v4.startedAt,
    ended_at: v4.endedAt,
    notes: v4.notes,
    owner_id: v4.ownerId,
    scribe_id: v4.scribeId,
    created_by: v4.createdBy,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    // Phase 4 — recording + CRM enrichment
    recording_url: v4.recording_url ?? null,
    transcript_url: v4.transcript_url ?? null,
    match_status: (v4.match_status as MatchStatus) ?? null,
    source: v4.source ?? null,
    attendee_emails: v4.attendee_emails ?? [],
    matched_company: v4.matchedCompany ?? null,
    matched_contacts: v4.matchedContacts ?? [],
    conference_id: v4.conference_id ?? null,
    conference_link: v4.conference_link ?? null,
  };
}

/**
 * Returns the proxy URL for streaming a meeting recording.
 * Uses /api prefix so it routes through the Vite dev proxy (web_api_server at /mastra/*).
 * The endpoint is Range-enabled; pass directly as <video src>.
 */
export function getMeetingRecordingUrl(meetingId: string): string {
  return `/api/v4/meetings/${meetingId}/recording`;
}

function transformV4MeetingSection(v4: V4MeetingSection): MeetingSection {
  return {
    id: v4.id,
    meeting_id: v4.meetingId,
    agenda_section_id: v4.agendaSectionId,
    name: v4.name,
    tool_type: v4.toolType as ToolType | null,
    sort_order: v4.sortOrder,
    duration_planned: v4.durationPlanned,
    duration_actual: v4.durationActual,
    started_at: v4.startedAt,
    ended_at: v4.endedAt,
  };
}

function transformV4MeetingAttendee(v4: V4MeetingAttendee): MeetingAttendee {
  return {
    id: v4.id,
    meeting_id: v4.meetingId,
    user_id: v4.userId,
    is_absent: v4.isAbsent,
    rating: v4.rating,
  };
}

function transformV4MeetingTicket(v4: V4MeetingTicket): MeetingTicket {
  return {
    id: v4.id,
    meeting_id: v4.meetingId,
    ticket_id: v4.ticketId,
    action: v4.action as TicketAction,
  };
}

function transformV4MeetingDetail(v4: V4MeetingDetail): MeetingDetail {
  return {
    ...transformV4Meeting(v4),
    sections: (v4.sections || []).map(transformV4MeetingSection),
    attendees: (v4.attendees || []).map(transformV4MeetingAttendee),
    tickets: (v4.tickets || []).map(transformV4MeetingTicket),
  };
}

// Transform input to camelCase for API
function transformCreateAgendaInput(input: CreateAgendaInput): Record<string, unknown> {
  const result: Record<string, unknown> = { title: input.title };
  if (input.team_id) result.teamId = input.team_id;
  return result;
}

function transformCreateMeetingInput(input: CreateMeetingInput): Record<string, unknown> {
  return {
    agenda_id: input.agenda_id,
    team_id: input.team_id,
    scheduled_at: input.scheduled_at,
    owner_id: input.owner_id,
    scribe_id: input.scribe_id,
  };
}

function transformUpdateMeetingInput(input: UpdateMeetingInput): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (input.title !== undefined) result.title = input.title;
  if (input.scheduled_at !== undefined) result.scheduled_at = input.scheduled_at;
  if (input.owner_id !== undefined) result.owner_id = input.owner_id;
  if (input.scribe_id !== undefined) result.scribe_id = input.scribe_id;
  if (input.notes !== undefined) result.notes = input.notes;
  return result;
}

function transformCreateSectionInput(input: CreateSectionInput): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (input.name !== undefined) result.name = input.name;
  if (input.tool_type !== undefined) result.tool_type = input.tool_type;
  if (input.duration_seconds !== undefined) result.duration_seconds = input.duration_seconds;
  if (input.is_visible !== undefined) result.is_visible = input.is_visible;
  if (input.is_locked !== undefined) result.is_locked = input.is_locked;
  return result;
}

function transformUpdateAttendeeInput(input: UpdateAttendeeInput): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (input.is_absent !== undefined) result.isAbsent = input.is_absent;
  if (input.rating !== undefined) result.rating = input.rating;
  return result;
}

// =============================================================================
// Agenda API Functions
// =============================================================================

export async function fetchAgendas(params: AgendasQueryParams = {}): Promise<AgendasResponse> {
  const queryParams: Record<string, string | number> = {};
  if (params.team_id) queryParams.teamId = params.team_id;
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;

  const response = await api.get<V4AgendasResponse>('/v4/agendas', queryParams);

  return {
    agendas: response.agendas.map(transformV4Agenda),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function fetchAgenda(id: string): Promise<AgendaWithSections> {
  const response = await api.get<V4AgendaWithSections>(`/v4/agendas/${id}`);
  return transformV4AgendaWithSections(response);
}

export async function createAgenda(input: CreateAgendaInput): Promise<Agenda> {
  const response = await api.post<V4Agenda>('/v4/agendas', transformCreateAgendaInput(input));
  return transformV4Agenda(response);
}

export async function updateAgenda(id: string, input: Partial<CreateAgendaInput>): Promise<Agenda> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.team_id !== undefined) body.team_id = input.team_id;

  const response = await api.put<V4Agenda>(`/v4/agendas/${id}`, body);
  return transformV4Agenda(response);
}

export async function deleteAgenda(id: string): Promise<{ deleted: boolean }> {
  return api.delete(`/v4/agendas/${id}`);
}

// =============================================================================
// Agenda Section API Functions
// =============================================================================

export async function addSection(agendaId: string, input: CreateSectionInput): Promise<AgendaSection> {
  const response = await api.post<V4AgendaSection>(
    `/v4/agendas/${agendaId}/sections`,
    transformCreateSectionInput(input)
  );
  return transformV4AgendaSection(response);
}

export async function updateSection(
  agendaId: string,
  sectionId: string,
  input: Partial<CreateSectionInput>
): Promise<AgendaSection> {
  const body = transformCreateSectionInput(input as CreateSectionInput);
  const response = await api.put<V4AgendaSection>(
    `/v4/agendas/${agendaId}/sections/${sectionId}`,
    body
  );
  return transformV4AgendaSection(response);
}

export async function deleteSection(agendaId: string, sectionId: string): Promise<{ deleted: boolean }> {
  return api.delete(`/v4/agendas/${agendaId}/sections/${sectionId}`);
}

export async function reorderSections(agendaId: string, sectionIds: string[]): Promise<void> {
  await api.patch(`/v4/agendas/${agendaId}/sections/reorder`, { section_ids: sectionIds });
}

// =============================================================================
// Meeting API Functions
// =============================================================================

export async function fetchMeetings(params: MeetingsQueryParams = {}): Promise<MeetingsResponse> {
  const queryParams: Record<string, string | number> = {};
  if (params.team_id) queryParams.teamId = params.team_id;
  if (params.type) queryParams.type = params.type;
  if (params.status) queryParams.status = params.status;
  if (params.date_from) queryParams.dateFrom = params.date_from;
  if (params.date_to) queryParams.dateTo = params.date_to;
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;
  if (params.sortKey) queryParams.sortKey = params.sortKey;
  if (params.sortDirection) queryParams.sortDirection = params.sortDirection;
  // Phase 4 — server-side filters
  if (params.q) queryParams.q = params.q;
  if (params.matchStatus) queryParams.matchStatus = params.matchStatus;
  if (params.source) queryParams.source = params.source;
  if (params.scope) queryParams.scope = params.scope;
  if (params.from) queryParams.from = params.from;
  if (params.to) queryParams.to = params.to;

  const response = await api.get<V4MeetingsResponse>('/v4/meetings', queryParams);

  return {
    meetings: response.meetings.map(transformV4Meeting),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function fetchMeetingTranscript(id: string): Promise<MeetingTranscript> {
  return api.get<MeetingTranscript>(`/v4/meetings/${id}/transcript`);
}

export async function fetchMeeting(id: string): Promise<MeetingDetail> {
  const response = await api.get<V4MeetingDetail>(`/v4/meetings/${id}`);
  return transformV4MeetingDetail(response);
}

export async function createMeeting(input: CreateMeetingInput): Promise<Meeting> {
  const response = await api.post<V4Meeting>('/v4/meetings', transformCreateMeetingInput(input));
  return transformV4Meeting(response);
}

export async function updateMeeting(id: string, input: UpdateMeetingInput): Promise<Meeting> {
  const response = await api.put<V4Meeting>(`/v4/meetings/${id}`, transformUpdateMeetingInput(input));
  return transformV4Meeting(response);
}

export async function deleteMeeting(id: string): Promise<{ deleted: boolean }> {
  return api.delete(`/v4/meetings/${id}`);
}

export async function startMeeting(id: string): Promise<Meeting> {
  const response = await api.patch<V4Meeting>(`/v4/meetings/${id}/start`);
  return transformV4Meeting(response);
}

export async function finishMeeting(id: string): Promise<Meeting> {
  const response = await api.patch<V4Meeting>(`/v4/meetings/${id}/finish`);
  return transformV4Meeting(response);
}

export async function cancelMeeting(id: string): Promise<Meeting> {
  const response = await api.patch<V4Meeting>(`/v4/meetings/${id}/cancel`);
  return transformV4Meeting(response);
}

// =============================================================================
// Attendee API Functions
// =============================================================================

export async function fetchAttendees(meetingId: string): Promise<MeetingAttendee[]> {
  const response = await api.get<{ attendees: V4MeetingAttendee[] }>(`/v4/meetings/${meetingId}/attendees`);
  return response.attendees.map(transformV4MeetingAttendee);
}

export async function updateAttendee(
  meetingId: string,
  attendeeId: string,
  input: UpdateAttendeeInput
): Promise<MeetingAttendee> {
  const response = await api.patch<V4MeetingAttendee>(
    `/v4/meetings/${meetingId}/attendees/${attendeeId}`,
    transformUpdateAttendeeInput(input)
  );
  return transformV4MeetingAttendee(response);
}

// =============================================================================
// Ticket API Functions
// =============================================================================

export async function fetchTickets(meetingId: string): Promise<MeetingTicket[]> {
  const response = await api.get<{ tickets: V4MeetingTicket[] }>(`/v4/meetings/${meetingId}/tickets`);
  return response.tickets.map(transformV4MeetingTicket);
}

export async function addTicket(meetingId: string, input: AddTicketInput): Promise<MeetingTicket> {
  const response = await api.post<V4MeetingTicket>(`/v4/meetings/${meetingId}/tickets`, {
    ticketId: input.ticket_id,
    action: input.action,
  });
  return transformV4MeetingTicket(response);
}

export async function updateTicket(
  meetingId: string,
  ticketId: string,
  action: TicketAction
): Promise<MeetingTicket> {
  const response = await api.put<V4MeetingTicket>(`/v4/meetings/${meetingId}/tickets/${ticketId}`, { action });
  return transformV4MeetingTicket(response);
}

export async function deleteTicket(meetingId: string, ticketId: string): Promise<{ deleted: boolean }> {
  return api.delete(`/v4/meetings/${meetingId}/tickets/${ticketId}`);
}

// =============================================================================
// Staff Users API Functions
// =============================================================================

export async function fetchStaffUsers(): Promise<StaffUser[]> {
  const response = await api.get<StaffUsersResponse>('/v4/users');
  return response.users;
}

// =============================================================================
// Google Calendar API Functions
// =============================================================================

export async function fetchCalendarEvents(
  params: { days?: number; calendarId?: string } = {}
): Promise<CalendarEventsResponse> {
  const queryParams: Record<string, string | number> = {};
  if (params.days !== undefined) queryParams.days = params.days;
  if (params.calendarId) queryParams.calendarId = params.calendarId;
  return api.get<CalendarEventsResponse>('/v4/meetings/calendar-events', queryParams);
}
